import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useUserDetailContext } from "../../UserDetailContext";
import { useDispatch } from "react-redux";
import { getInterviewers } from "../../../Redux/Reducers/JobReducer";
import { getGoogleLocalPlaces } from "../../../Redux/helpers/JobsApi";
import {
  getProfessionalEvents,
  postProfessionalEvents,
  updateProfessionalEvents,
} from "../../../Redux/Reducers/calendarReducer";
import { getConnectedReducer } from "../../../Redux/Reducers/ContactReducer";
import { getExperienceReducer } from "../../../Redux/Reducers/ProfileReducer";
import TimePicker from "../../../Components/Utilities/TimePicker";

// ─── Shared z-index for all popover/popper dropdowns inside this drawer ───────
const DROPDOWN_Z_INDEX = 9999;

const ProfessionalShedule = ({ open, onClose, selectedEvent, selectedDate }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const companyCalender =
    useSelector((state) => state?.calender?.professionalEventsData) || [];
  const formData = Array.isArray(selectedEvent)
    ? selectedEvent[0]
    : selectedEvent;

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [pendingFormattedData, setPendingFormattedData] = useState(null);

  const getInitialTime = () => {
    const now = dayjs();
    if (dayjs(selectedDate).isSame(now, "day")) {
      return now.minute() > 30
        ? now.add(1, "hour").startOf("hour")
        : now.startOf("hour");
    }
    return dayjs(selectedDate).hour(9).startOf("hour");
  };

  const [eventData, setEventData] = useState({
    companyName: formData?.companyName || "",
    title: formData?.title || "",
    description: formData?.description || "",
    attendees: formData?.attendees || [],
    eventDate: formData?.startTime
      ? dayjs(formData.startTime)
      : dayjs(selectedDate),
    startTime: formData?.startTime
      ? dayjs(formData.startTime)
      : getInitialTime(),
    endTime: formData?.endTime
      ? dayjs(formData.endTime)
      : getInitialTime().add(1, "hour"),
    companyDomain: formData?.companyDomain || "",
    timeZone: formData?.timeZone || "Asia/Kolkata",
    meetLink: formData?.meetLink || "",
    location: formData?.location || "",
    meetingType: formData?.meetingType || "Remote",
  });

  const [timeError, setTimeError] = useState(null);
  const [locations, setLocations] = useState([]);
  const ProfessionalExperience =
    useSelector((state) => state?.ProfileArray?.experienceData?.data) || [];
  const { userDetails } = useUserDetailContext();

  const interviewers = useSelector((state) => state?.jobs?.interviewers || []);
  const interviwersList = interviewers?.interviewers || [];

  useEffect(() => {
    if (selectedEvent) {
      const formData = Array.isArray(selectedEvent)
        ? selectedEvent[0]
        : selectedEvent;
      setEventData({
        id: formData?.id || null,
        companyName: formData?.companyName || "",
        title: formData?.title || "",
        description: formData?.description || "",
        attendees:
          formData?.attendees?.map((attendee) => ({
            name: attendee.name,
            email: attendee.email,
            profile_pic: attendee.profile_pic,
            user_ref_id: attendee.user_ref_id,
          })) || [],
        eventDate: formData?.startTime
          ? dayjs(formData.startTime)
          : dayjs(selectedDate),
        startTime: formData?.startTime
          ? dayjs(formData.startTime)
          : getInitialTime(),
        endTime: formData?.endTime
          ? dayjs(formData.endTime)
          : getInitialTime().add(1, "hour"),
        companyDomain: formData?.companyDomain || "",
        timeZone: formData?.timeZone || "Asia/Kolkata",
        meetLink: formData?.meetLink || "",
        location: formData?.location || "",
        meetingType: formData?.meetingType || "Remote",
      });
    } else {
      const defaultCompany = getVerifiedCurrentCompany(ProfessionalExperience)?.[0];
      setEventData({
        id: "",
        companyName: defaultCompany?.company_name || "",
        title: "",
        description: "",
        attendees: [],
        eventDate: dayjs(selectedDate),
        startTime: getInitialTime(),
        endTime: getInitialTime().add(1, "hour"),
        companyDomain: defaultCompany?.company_domain || "",
        timeZone: "Asia/Kolkata",
        meetLink: "",
        location: "",
        meetingType: "",
      });
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (open) {
      let companyNameToFetch = "";
      if (eventData.companyName) {
        companyNameToFetch = eventData.companyName;
      } else {
        const defaultCompany = getVerifiedCurrentCompany(ProfessionalExperience)?.[0];
        companyNameToFetch = defaultCompany?.company_name || "";
      }
      if (companyNameToFetch) fetchInterviewers(companyNameToFetch);
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && eventData.companyName) {
      fetchInterviewers(eventData.companyName);
    }
  }, [eventData.companyName, open, dispatch]);

  const fetchInterviewers = async (companyName) => {
    if (companyName) {
      try {
        const params = { company_name: companyName };
        await dispatch(getInterviewers(params));
      } catch (error) {
        console.error("Failed to fetch interviewers:", error);
      }
    }
  };

  const handleCompanyChange = (e) => {
    const selectedCompanyName = e.target.value;
    const selectedCompany = ProfessionalExperience?.find(
      (company) => company?.company_name === selectedCompanyName
    );
    const updatedEventData = {
      ...eventData,
      companyName: selectedCompany?.company_name || "",
      companyDomain: selectedCompany?.company_domain || "",
      companyLogo: selectedCompany?.company_logo || "",
    };
    setEventData(updatedEventData);
    if (selectedCompany?.company_name) {
      fetchInterviewers(selectedCompany.company_name);
    }
  };

  const saveEvent = async (formattedData) => {
    if (loading) return;
    setLoading(true);
    try {
      if (eventData.id) {
        const newPayload = {
          ...formattedData,
          type:
            new Date(selectedEvent?.startTime)?.getTime() ==
              new Date(formattedData?.startTime)?.getTime() &&
            new Date(selectedEvent?.endTime)?.getTime() ==
              new Date(formattedData?.endTime)?.getTime()
              ? "not_reschedule"
              : "reschedule",
        };
        await dispatch(updateProfessionalEvents(newPayload));
      } else {
        await dispatch(postProfessionalEvents(formattedData));
      }
      await dispatch(getProfessionalEvents());
      setEventData({
        companyName: "",
        title: "",
        description: "",
        attendees: [],
        eventDate: dayjs(selectedDate),
        startTime: getInitialTime(),
        endTime: getInitialTime().add(1, "hour"),
        companyDomain: "",
        timeZone: "Asia/Kolkata",
        meetLink: "",
        location: "",
        meetingType: "Remote",
      });
      onClose();
    } catch (error) {
      console.error("Failed to save event:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;
    const now = dayjs();

    if (!eventData.startTime || !eventData.endTime || !eventData.eventDate) {
      setTimeError("Please select date and both start and end times");
      return;
    }

    const startDateTime = eventData.eventDate
      .hour(eventData.startTime.hour())
      .minute(eventData.startTime.minute())
      .second(0);

    const endDateTime = eventData.eventDate
      .hour(eventData.endTime.hour())
      .minute(eventData.endTime.minute())
      .second(0);

    let finalEndDateTime = endDateTime;
    if (endDateTime.isBefore(startDateTime)) {
      finalEndDateTime = endDateTime.add(1, "day");
    }

    if (startDateTime.isBefore(now)) {
      setTimeError("Cannot schedule events in the past");
      return;
    }

    if (finalEndDateTime.isBefore(startDateTime)) {
      setTimeError("End time must be after start time");
      return;
    }

    const formattedData = {
      ...eventData,
      startTime: startDateTime.toISOString(),
      endTime: finalEndDateTime.toISOString(),
      attendees: eventData.attendees.map((attendee) => ({
        name: attendee.name,
        user_ref_id: attendee.user_ref_id,
        email: attendee.email,
        profile_pic: attendee.profile_pic,
      })),
    };
    delete formattedData.eventDate;

    const excludeId = eventData.id || null;
    const conflictingEvent = companyCalender.find((event) => {
      if (excludeId && event.id === excludeId) return false;
      const existingStart = dayjs(event.startTime);
      const existingEnd = dayjs(event.endTime);
      return startDateTime.isBefore(existingEnd) && finalEndDateTime.isAfter(existingStart);
    });

    if (conflictingEvent) {
      setConflictInfo({
        startTime: dayjs(conflictingEvent.startTime).format("HH:mm:ss"),
        endTime: dayjs(conflictingEvent.endTime).format("HH:mm:ss"),
      });
      setPendingFormattedData(formattedData);
      setConflictDialogOpen(true);
      return;
    }

    await saveEvent(formattedData);
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    const now = dayjs();
    if (newDate.isBefore(now, "day")) {
      setTimeError("Cannot select a past date");
      return;
    }
    setEventData({ ...eventData, eventDate: newDate });
    setTimeError(null);
  };

  const handleStartTimeChange = (newTime) => {
    if (!newTime) return;
    setEventData({
      ...eventData,
      startTime: newTime,
      endTime: newTime.isAfter(eventData.endTime)
        ? newTime.add(1, "hour")
        : eventData.endTime,
    });
    setTimeError(null);
  };

  const handleEndTimeChange = (newTime) => {
    if (!newTime) return;
    setEventData({ ...eventData, endTime: newTime });
    setTimeError(null);
  };

  const onChangeLocation = async (input) => {
    try {
      if (!input || typeof input !== "string") {
        setLocations([]);
        return;
      }
      if (input.length > 2) {
        setLocations([]);
        const response = await getGoogleLocalPlaces(input);
        if (Array.isArray(response)) {
          setLocations(response?.map((place) => ({ description: place, placeId: place })));
        } else if (response?.predictions) {
          const validLocations = response.predictions
            .filter((place) => place?.description)
            .map((place) => ({
              description: place.description,
              placeId: place.place_id || place.description,
            }));
          setLocations(validLocations);
        } else {
          setLocations([]);
        }
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  const getVerifiedCurrentCompany = (companies) => {
    return (
      companies?.filter(
        (exp) =>
          exp.is_verified &&
          exp.is_currently_working &&
          exp.companyByName?.is_verified
      ) || []
    );
  };

  useEffect(() => {
    dispatch(getConnectedReducer());
    dispatch(getExperienceReducer());
  }, []);

  // ─── Reusable MenuProps / popperProps applied to every dropdown ──────────────
  const dropdownMenuProps = {
    MenuProps: {
      sx: { zIndex: DROPDOWN_Z_INDEX },
      container: document.body,
      anchorOrigin:    { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top",    horizontal: "left" },
    },
  };

  const dropdownPopperProps = {
    componentsProps: {
      popper: {
        sx:       { zIndex: DROPDOWN_Z_INDEX },
        style:    { zIndex: DROPDOWN_Z_INDEX },
        container: document.body,
        modifiers: [
          { name: "preventOverflow", enabled: true, options: { boundary: "window" } },
        ],
      },
    },
  };

  // ────────────────────────────────────────────────────────────────────────────

  return (
    // ✅ Plain fixed Box — identical pattern to the working SchedulePersonalEvent.
    //    SwipeableDrawer was causing the backdrop to sit on top of form inputs.
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: open ? 0 : "-30vw",      // slide in/out like the personal event panel
        width: "25vw",
        height: "100%",
        backgroundColor: "#fff",
        boxShadow: "-2px 0 10px rgba(0,0,0,0.15)",
        transition: "right 0.3s ease",
        zIndex: 2200,                    // above calendar drawers (1900–2100)
        overflowY: "auto",
        scrollbarWidth: "none",
      }}
    >
      <Box sx={{ p: 3, pb: 6 }}>
        {/* ── Header ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6">
            {eventData?.id ? "Edit Event" : "Schedule Event"}
          </Typography>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Form ── */}
        <Box sx={{ display: "grid", gap: 3 }}>

          {/* Company Name */}
          <TextField
            select
            label="Company Name"
            fullWidth
            required
            disabled={loading}
            value={
              eventData?.companyName ||
              getVerifiedCurrentCompany(ProfessionalExperience)?.[0]?.company_name ||
              ""
            }
            onChange={handleCompanyChange}
            SelectProps={dropdownMenuProps}   // ✅ fixes dropdown z-index
          >
            {getVerifiedCurrentCompany(ProfessionalExperience).map((company) => (
              <MenuItem
                key={company?.id}
                value={company?.company_name}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {company?.company_logo && (
                    <img
                      src={company?.company_logo}
                      alt={company?.company_name}
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  )}
                  <Typography>{company?.company_name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Title */}
          <TextField
            label="Title"
            fullWidth
            disabled={loading}
            value={eventData?.title}
            onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
          />

          {/* Description */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            value={eventData?.description}
            onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
          />

          {/* Meeting Type */}
          <TextField
            select
            label="Meeting Type"
            fullWidth
            disabled={loading}
            value={eventData.meetingType}
            onChange={(e) => setEventData({ ...eventData, meetingType: e.target.value })}
            SelectProps={dropdownMenuProps}   // ✅ fixes dropdown z-index
          >
            <MenuItem value="Remote">Online</MenuItem>
            <MenuItem value="Others">Offline</MenuItem>
          </TextField>

          {/* Meeting Link OR Location */}
          {eventData.meetingType === "Remote" ? (
            <TextField
              label="Meeting Link"
              fullWidth
              disabled={loading}
              value={eventData.meetLink}
              onChange={(e) => setEventData({ ...eventData, meetLink: e.target.value })}
            />
          ) : (
            <Autocomplete
              freeSolo
              options={locations}
              getOptionLabel={(option) => {
                if (!option) return "";
                return typeof option === "string" ? option : option.description || "";
              }}
              value={eventData?.location || null}
              onChange={(event, newValue) => {
                setEventData({
                  ...eventData,
                  location: typeof newValue === "string" ? newValue : newValue?.description || "",
                });
              }}
              onInputChange={(event, newValue) => {
                if (event?.type === "change") onChangeLocation(newValue);
              }}
              filterOptions={(options, params) => {
                return options.filter((option) => {
                  const value = typeof option === "string" ? option : option.description;
                  return value.toLowerCase().includes(params.inputValue.toLowerCase());
                });
              }}
              disabled={loading}
              {...dropdownPopperProps}          // ✅ fixes popper z-index
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  required
                  variant="outlined"
                  fullWidth
                  disabled={loading}
                  InputProps={{ ...params.InputProps, style: { fontSize: "0.875rem" } }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography variant="body2">
                    {typeof option === "string" ? option : option.description}
                  </Typography>
                </li>
              )}
              noOptionsText="No locations found"
              loading={locations.length === 0}
              loadingText="Searching locations..."
            />
          )}

          {/* Date & Time */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <DatePicker
                label="Event Date"
                value={eventData.eventDate}
                onChange={handleDateChange}
                minDate={dayjs()}
                disabled={loading}
                slotProps={{
                  textField: { fullWidth: true, sx: { mb: 2 } },
                  popper: {               // ✅ fixes calendar popper z-index
                    sx:        { zIndex: DROPDOWN_Z_INDEX },
                    container: document.body,
                    modifiers: [
                      { name: "preventOverflow", enabled: true, options: { boundary: "window" } },
                    ],
                  },
                }}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <TimePicker
                  label="Start Time"
                  value={eventData.startTime}
                  onChange={handleStartTimeChange}
                  error={!!timeError}
                  helperText={timeError}
                  disabled={loading}
                  componentsProps={{
                    popper: { sx: { zIndex: DROPDOWN_Z_INDEX }, container: document.body },
                  }}
                />
                <TimePicker
                  label="End Time"
                  value={eventData.endTime}
                  onChange={handleEndTimeChange}
                  error={!!timeError}
                  helperText={timeError}
                  disabled={loading}
                  componentsProps={{
                    popper: { sx: { zIndex: DROPDOWN_Z_INDEX }, container: document.body },
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>

          {timeError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {timeError}
            </Typography>
          )}

          {/* Attendees */}
          <Autocomplete
            multiple
            options={interviwersList}
            getOptionLabel={(option) =>
              `${option?.first_name || ""} ${option?.last_name || ""}${
                option?.email ? ` (${option?.email})` : ""
              }`
            }
            filterOptions={(options, { inputValue }) => {
              const input = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  (option.first_name?.toLowerCase() || "").includes(input) ||
                  (option.last_name?.toLowerCase() || "").includes(input) ||
                  (option.email?.toLowerCase() || "").includes(input)
              );
            }}
            value={interviwersList?.filter((interviewer) =>
              eventData?.attendees?.some((attendee) => attendee?.email === interviewer?.email)
            )}
            onChange={(_, newValue) => {
              const attendees = newValue?.map((interviewer) => ({
                name: [interviewer.first_name, interviewer.last_name].filter(Boolean).join(" "),
                email: interviewer?.email,
                user_ref_id: interviewer?.reference_id,
                profile_pic:
                  interviewer?.profile_url ||
                  "https://static-00.iconduck.com/assets.00/avatar-default-icon-2048x2048-h6w375ur.png",
              }));
              setEventData({ ...eventData, attendees });
            }}
            disabled={loading}
            {...dropdownPopperProps}           // ✅ fixes popper z-index
            renderTags={() => null}
            renderOption={(props, option) => (
              <li {...props}>
                <img
                  src={
                    option.profile_url ||
                    "https://static-00.iconduck.com/assets.00/avatar-default-icon-2048x2048-h6w375ur.png"
                  }
                  alt={`${option.first_name || ""} ${option.last_name || ""}`}
                  style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8, objectFit: "cover" }}
                />
                <span>
                  {option.first_name || ""} {option.last_name || ""}
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Attendees"
                placeholder="Search by name or email"
                disabled={loading}
              />
            )}
          />

          {/* Selected attendees chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 150, overflowY: "auto", p: 1, borderRadius: 1 }}>
            {eventData?.attendees?.map((attendee, index) => (
              <Box
                key={attendee.email}
                sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "background.paper", borderRadius: 4, boxShadow: 1 }}
              >
                <img
                  src={
                    attendee.profile_pic ||
                    "https://www.shutterstock.com/image-vector/default-avatar-profile-icon-transparent-600nw-2463868853.jpg"
                  }
                  alt={attendee.name}
                  style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
                />
                <Typography variant="body2">{attendee.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newAttendees = [...eventData.attendees];
                    newAttendees.splice(index, 1);
                    setEventData({ ...eventData, attendees: newAttendees });
                  }}
                  sx={{ ml: 0.5 }}
                  disabled={loading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Submit */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              eventData?.id ? "Update Event" : "Schedule Event"
            )}
          </Button>
        </Box>
      </Box>

      {/* ── Time Slot Conflict Dialog ── */}
      <Dialog
        open={conflictDialogOpen}
        onClose={() => setConflictDialogOpen(false)}
        // Dialog already renders at zIndex 1300+; keep it above the panel
        sx={{ zIndex: DROPDOWN_Z_INDEX }}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 380, backgroundColor: "#ffffff" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 0 }}>
          Time Slot Unavailable
          <IconButton
            onClick={() => setConflictDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ color: "text.primary", fontSize: "0.95rem" }}>
            The time slot from <strong>{conflictInfo?.startTime}</strong> to{" "}
            <strong>{conflictInfo?.endTime}</strong> is already booked. Are you sure you
            want to select this time?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConflictDialogOpen(false)}
            variant="outlined"
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 600, borderColor: "#ccc", color: "text.primary", borderRadius: 2, flex: 1 }}
          >
            Change Time
          </Button>
          <Button
            onClick={async () => {
              setConflictDialogOpen(false);
              await saveEvent(pendingFormattedData);
              setPendingFormattedData(null);
            }}
            variant="contained"
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#0044C6", borderRadius: 2, flex: 1, "&:hover": { backgroundColor: "#0035a0" } }}
          >
            Select Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalShedule;
